import type * as geojson from 'geojson';

import {GridSampling} from './datasets';
import {Projector} from './grid';

/**
 * TypeScript port of the backend's pixel-weighted basin averaging
 * (PixelWeightedAverageBasinAverager / ProjectedPixelWeightedAverageBasinAverager in
 * backend/workers/series_processor_v2/lib/processors/job.py). Weights are the
 * fractional overlap of each grid cell with the basin polygon, estimated by testing
 * a SUB_PIXEL_FACTOR × SUB_PIXEL_FACTOR subgrid per cell, multiplied by cos(latitude)
 * on geographic grids to account for cell area shrinking toward the poles.
 */
const SUB_PIXEL_FACTOR = 10;

/** Matches the backend's shapely `.buffer(0.01)` (degrees) for point sites. */
const POINT_BUFFER_DEGREES = 0.01;
const POINT_BUFFER_SEGMENTS = 16;

type Ring = Array<[number, number]>;

export type WeightGrid = {
  /** Row-major (y, x) weights over the index window the grid was built for. */
  weights: Float64Array;
  rows: number;
  cols: number;
  /** Sum of all weights; 0 never escapes (nearest-cell fallback guarantees > 0). */
  totalWeight: number;
};

function geometryToRings(geometry: geojson.Geometry): Ring[] {
  switch (geometry.type) {
    case 'Polygon':
      return geometry.coordinates as Ring[];
    case 'MultiPolygon':
      return (geometry.coordinates as Ring[][]).flat();
    case 'Point': {
      const [lon, lat] = geometry.coordinates as [number, number];
      const ring: Ring = [];
      for (let i = 0; i < POINT_BUFFER_SEGMENTS; i += 1) {
        const angle = (2 * Math.PI * i) / POINT_BUFFER_SEGMENTS;
        ring.push([
          lon + POINT_BUFFER_DEGREES * Math.cos(angle),
          lat + POINT_BUFFER_DEGREES * Math.sin(angle),
        ]);
      }
      ring.push(ring[0]);
      return [ring];
    }
    default:
      throw new Error(`Unsupported basin geometry type: ${geometry.type}`);
  }
}

/** Area-weighted centroid over all rings (even-odd: holes subtract naturally). */
function ringsCentroid(rings: Ring[]): [number, number] {
  let areaSum = 0;
  let cx = 0;
  let cy = 0;
  for (const ring of rings) {
    for (let i = 0; i < ring.length - 1; i += 1) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[i + 1];
      const cross = x1 * y2 - x2 * y1;
      areaSum += cross;
      cx += (x1 + x2) * cross;
      cy += (y1 + y2) * cross;
    }
  }
  if (Math.abs(areaSum) < 1e-12) {
    // Degenerate polygon: fall back to the vertex mean.
    let n = 0;
    let sx = 0;
    let sy = 0;
    for (const ring of rings) {
      for (const [x, y] of ring) {
        sx += x;
        sy += y;
        n += 1;
      }
    }
    return [sx / n, sy / n];
  }
  return [cx / (3 * areaSum), cy / (3 * areaSum)];
}

/** Bounding box over all rings as [minX, minY, maxX, maxY]. */
export function ringsBbox(rings: Ring[]): [number, number, number, number] {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const ring of rings) {
    for (const [x, y] of ring) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  return [minX, minY, maxX, maxY];
}

export function geometryRings(
  geometry: geojson.Geometry,
  projector: Projector | null
): Ring[] {
  const rings = geometryToRings(geometry);
  if (!projector) {
    return rings;
  }
  return rings.map((ring) => ring.map((coord) => projector(coord)));
}

/**
 * Fractional overlap of each cell in the window with the polygon, via even-odd
 * scanline filling of a SUB_PIXEL_FACTOR-times-finer subgrid. A scanline fill (edge
 * crossings sorted per row) keeps this O(rows × edges) instead of running a
 * point-in-polygon test for every subcell of every basin-sized window.
 */
function fractionalOverlap(
  rings: Ring[],
  yCenters: Float64Array,
  xCenters: Float64Array
): Float64Array {
  const rows = yCenters.length;
  const cols = xCenters.length;
  const frac = new Float64Array(rows * cols);
  if (rows === 0 || cols === 0) {
    return frac;
  }
  const dy = rows > 1 ? (yCenters[rows - 1] - yCenters[0]) / (rows - 1) : 1;
  const dx = cols > 1 ? (xCenters[cols - 1] - xCenters[0]) / (cols - 1) : 1;
  // Continuous subcell coordinate: cell j spans s ∈ [j*f, (j+1)*f) regardless of
  // whether the x axis ascends or descends.
  const subX = (x: number) => ((x - xCenters[0]) / dx + 0.5) * SUB_PIXEL_FACTOR;
  const subCellCount = cols * SUB_PIXEL_FACTOR;
  const hitIncrement = 1 / (SUB_PIXEL_FACTOR * SUB_PIXEL_FACTOR);

  const crossings: number[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let sub = 0; sub < SUB_PIXEL_FACTOR; sub += 1) {
      const yScan = yCenters[row] + dy * ((sub + 0.5) / SUB_PIXEL_FACTOR - 0.5);
      crossings.length = 0;
      for (const ring of rings) {
        for (let i = 0; i < ring.length - 1; i += 1) {
          const [x1, y1] = ring[i];
          const [x2, y2] = ring[i + 1];
          // Half-open vertex rule so a scanline through a vertex counts once.
          if (y1 <= yScan !== y2 <= yScan) {
            crossings.push(x1 + ((yScan - y1) * (x2 - x1)) / (y2 - y1));
          }
        }
      }
      if (crossings.length < 2) {
        continue;
      }
      crossings.sort((a, b) => a - b);
      for (let c = 0; c + 1 < crossings.length; c += 2) {
        const sa = subX(crossings[c]);
        const sb = subX(crossings[c + 1]);
        const [sMin, sMax] = sa <= sb ? [sa, sb] : [sb, sa];
        // Subcell u (center u + 0.5) is inside when sMin ≤ u + 0.5 ≤ sMax.
        const uStart = Math.max(0, Math.ceil(sMin - 0.5));
        const uEnd = Math.min(subCellCount - 1, Math.floor(sMax - 0.5));
        for (let u = uStart; u <= uEnd; u += 1) {
          frac[row * cols + Math.floor(u / SUB_PIXEL_FACTOR)] += hitIncrement;
        }
      }
    }
  }
  return frac;
}

/** Weight 1 on the single cell whose center is nearest the point, 0 elsewhere. */
function nearestCellWeights(
  point: [number, number],
  yCenters: Float64Array,
  xCenters: Float64Array
): WeightGrid {
  const rows = yCenters.length;
  const cols = xCenters.length;
  const weights = new Float64Array(rows * cols);
  let best = 0;
  let bestDistance = Infinity;
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const distance = (yCenters[row] - point[1]) ** 2 + (xCenters[col] - point[0]) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = row * cols + col;
      }
    }
  }
  weights[best] = 1;
  return {weights, rows, cols, totalWeight: 1};
}

export function computeBasinWeights(
  geometry: geojson.Geometry,
  yCenters: Float64Array,
  xCenters: Float64Array,
  gridKind: 'geographic' | 'projected',
  projector: Projector | null,
  sampling: GridSampling = 'native'
): WeightGrid {
  if (sampling === 'h4' && geometry.type === 'Point') {
    // The H4 pipeline samples point features as exactly the containing cell
    // (get_geometry_pixel_intersection_fractions returns a 1×1 mask), not a
    // buffered polygon that can straddle cells on fine grids like HRRR's.
    const lonLat = geometry.coordinates as [number, number];
    const point = gridKind === 'projected' && projector ? projector(lonLat) : lonLat;
    return nearestCellWeights(point, yCenters, xCenters);
  }

  const rings = geometryRings(geometry, gridKind === 'projected' ? projector : null);
  const rows = yCenters.length;
  const cols = xCenters.length;
  const weights = fractionalOverlap(rings, yCenters, xCenters);

  // The H4 pipeline weights purely by intersection fraction
  // (pixel_area_intersection_weighted_band_means); cos-latitude area weighting
  // only exists in the H5 averager this module ports.
  if (gridKind === 'geographic' && sampling === 'native') {
    for (let row = 0; row < rows; row += 1) {
      const latWeight = Math.cos((yCenters[row] * Math.PI) / 180);
      for (let col = 0; col < cols; col += 1) {
        weights[row * cols + col] *= latWeight;
      }
    }
  }

  let totalWeight = 0;
  for (let i = 0; i < weights.length; i += 1) {
    totalWeight += weights[i];
  }

  if (totalWeight === 0 && rows > 0 && cols > 0) {
    // Sub-pixel basins can miss every subcell center; extract the value at the
    // cell nearest the basin centroid instead, matching the backend fallback.
    return nearestCellWeights(ringsCentroid(rings), yCenters, xCenters);
  }

  return {weights, rows, cols, totalWeight};
}
